function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    // Prevent scroll during node insertion - MOBILE FIX
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    
    // Temporarily make the new node invisible to prevent scroll-to-view behavior
    newNode.style.visibility = 'hidden';
    newNode.style.position = 'absolute';

    oldNode.parentNode.insertBefore(newNode, oldNode);

    // Restore scroll position immediately
    window.scrollTo(scrollX, scrollY);
    
    // Make visible and restore layout after a frame
    requestAnimationFrame(() => {
      newNode.style.visibility = '';
      newNode.style.position = '';
    });

    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

// js/custom-element/section/product-list/product-item.js
class ProductItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Use event delegation instead of individual listeners
    this.addEventListener('change', this._handleSwatchChange, {
      capture: true,  // Use capture to ensure we catch events early
      passive: false  // Allow preventDefault if needed
    });
  }

  _handleSwatchChange(event) {
    // Check if the changed element is a color swatch radio
    const swatchRadio = event.target.closest('.color-swatch__radio');
    if (!swatchRadio) return;

    // Prevent event from bubbling up or being handled multiple times
    event.stopPropagation();

    // Find the exact product item that contains this swatch
    const productItem = swatchRadio.closest('.product-item');
    if (!productItem) return;

    // Ensure we're only working within this specific product item
    const primaryImageList = Array.from(productItem.querySelectorAll(".product-item__primary-image"));
    
    // Update product links
    const productLinks = productItem.querySelectorAll(`[href*="/products"]`);
    productLinks.forEach((link) => {
      const url = link.tagName === "A" 
        ? new URL(link.href) 
        : new URL(link.getAttribute("href"), `https://${window.themeVariables.routes.host}`);
      
      url.searchParams.set("variant", swatchRadio.getAttribute("data-variant-id"));
      link.setAttribute("href", url.toString());
    });

    // Check if the swatch has a price data attribute and update the price if it does
    const colorSwatch = swatchRadio.closest('.color-swatch');
    if (colorSwatch && colorSwatch.hasAttribute('data-variant-price')) {
      const variantPrice = colorSwatch.getAttribute('data-variant-price');
      const comparePrice = colorSwatch.getAttribute('data-variant-compare-price');
      
      // Find all price elements within this product item
      const priceElements = productItem.querySelectorAll('.card-information .price, .product-item-meta__price');
      
      if (priceElements.length > 0) {
        priceElements.forEach((priceElement) => {
          this._updatePriceDisplay(priceElement, variantPrice, comparePrice);
        });
      }
    }

    // Image swapping logic
    if (swatchRadio.hasAttribute("data-variant-featured-media")) {
      const newImage = primaryImageList.find((image) => 
        image.getAttribute("data-media-id") === swatchRadio.getAttribute("data-variant-featured-media")
      );

      if (newImage) {
        newImage.setAttribute("loading", "eager");
        newImage.removeAttribute("hidden");
        newImage.style.opacity = "1";

        // Hide other images
        primaryImageList
          .filter((image) => image !== newImage)
          .forEach((image) => {
            image.setAttribute("hidden", "");
            image.style.opacity = "0";
          });
      }

      // Update active swatch
      const swatchList = productItem.querySelector(".product-item-meta__swatch-list");
      if (swatchList) {
        swatchList.querySelectorAll(".color-swatch__item.is-active").forEach(item => {
          item.classList.remove("is-active");
        });

        const labelSibling = swatchRadio.nextElementSibling;
        if (labelSibling && labelSibling.classList.contains("color-swatch__item")) {
          labelSibling.classList.add("is-active");
        }
      }
    }
  }

  _updatePriceDisplay(priceElement, variantPrice, comparePrice) {
    const formattedPrice = this._formatPrice(variantPrice);
    const isOnSale = comparePrice && parseInt(comparePrice) > parseInt(variantPrice);
    
    // Update the main price container class
    if (isOnSale) {
      priceElement.classList.add('price--on-sale');
    } else {
      priceElement.classList.remove('price--on-sale');
    }
    
    // Update the regular price (always visible for non-sale items)
    const regularPriceElement = priceElement.querySelector('.price__regular .price-item--regular');
    if (regularPriceElement) {
      if (isOnSale) {
        // For sale items, regular price shows the sale price
        regularPriceElement.innerHTML = formattedPrice;
      } else {
        // For non-sale items, regular price shows the regular price
        regularPriceElement.innerHTML = formattedPrice;
      }
    }
    
    // Update the sale price element
    const salePriceElement = priceElement.querySelector('.price__sale .price-item--sale');
    if (salePriceElement) {
      salePriceElement.innerHTML = formattedPrice;
    }
    
    // Handle the compare price (the crossed-out price)
    const comparePriceElement = priceElement.querySelector('.price__sale .compare-group .price-item--regular');
    if (comparePriceElement) {
      if (isOnSale && comparePrice) {
        // Show the higher compare price crossed out
        const formattedComparePrice = this._formatPrice(comparePrice);
        comparePriceElement.innerHTML = formattedComparePrice;
      } else {
        // Clear the compare price for non-sale items
        comparePriceElement.innerHTML = '';
      }
    }
  }

  // Helper method to format price according to the store's currency format
  _formatPrice(price) {
    // Convert price to number if it's a string
    const priceValue = typeof price === 'string' ? parseInt(price, 10) : price;
    
    // Use the store's money format if available
    if (window.themeVariables && window.themeVariables.moneyFormat) {
      return window.themeVariables.moneyFormat.replace('{{amount}}', (priceValue / 100).toFixed(2));
    }
    
    // Try to find an existing price element to match its format
    const existingPriceElement = this.querySelector('.price__regular .price-item--regular');
    if (existingPriceElement) {
      const existingPrice = existingPriceElement.textContent;
      const currencySymbol = existingPrice.replace(/[0-9,.]/g, '').trim();
      return existingPrice.includes('.') 
        ? `${currencySymbol}${(priceValue / 100).toFixed(2)}`
        : `${currencySymbol}${Math.floor(priceValue / 100)}`;
    }
    
    // Default formatting if no other options are available
    return '$' + (priceValue / 100).toFixed(2);
  }
}

customElements.define("product-item", ProductItem);

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Get all buttons with filter-toggle class instead of just one
    this.buttons = this.querySelectorAll(".filter-toggle");
    this.drawer = this.querySelector("#filterDrawer");
    this.closeButton = this.querySelector(".custom-filter__close");
    this.desktopLabel = this.querySelector(".desktop-facets__open-label");
    this.mainFacetsContainer = this.querySelector('.mobile-facets__main:not(.no-submenu-slidein)');
    
    // Add click event listeners to all filter toggle buttons
    this.buttons.forEach(button => {
      button.addEventListener("click", () => this.toggleDrawer());
    });
    
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.toggleDrawer());
    }
    
    // Mobile-specific behavior for submenu handling
    this.initMobileDetails();
    
    // Add keyboard handling
    this.drawer.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.toggleDrawer(false); // Close drawer on escape
      }
    });
  }

  // This method only affects mobile submenu behavior
  initMobileDetails() {
    // Add click handlers to summaries to handle submenu opening
    const summaries = this.querySelectorAll('.mobile-facets__summary:not(.no-slidein)');
    console.warn('summaries', summaries)
    summaries.forEach(summary => {
      summary.addEventListener('click', (event) => {
        const details = summary.closest('details');
        
        if (!details.hasAttribute('open')) {
          // Opening the submenu
          event.preventDefault();
          
          // First close any open details
          this.querySelectorAll('.mobile-facets__details[open]').forEach(openDetails => {
            if (openDetails !== details) {
              openDetails.removeAttribute('open');
              openDetails.classList.remove('menu-opening');
            }
          });
          
          // Then open this one with proper classes
          details.setAttribute('open', '');
          details.classList.add('menu-opening');
          
          // Add the submenu-open class to the main container
          console.log('this.mainFacetsContainer',this.mainFacetsContainer)
          if (this.mainFacetsContainer) {
            this.mainFacetsContainer.classList.add('submenu-open');
          }
        }
      });
    });
    
    // Handle close buttons in submenus
    const closeButtons = this.querySelectorAll('.mobile-facets__close-button');
    closeButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const details = button.closest('details');
        if (details) {
          details.classList.remove('menu-opening');
          
          // Use setTimeout to allow animation to complete before closing
          setTimeout(() => {
            details.removeAttribute('open');
            
            // Remove submenu-open class if no other details are open
            if (this.mainFacetsContainer) {
              const anyOpen = Array.from(this.querySelectorAll('details[open]')).length > 0;
              if (!anyOpen) {
                this.mainFacetsContainer.classList.remove('submenu-open');
              }
            }
          }, 300); // Match transition duration
        }
      });
    });
    
    // Handle apply buttons in submenus to close entire drawer
    const applyButtons = this.querySelectorAll('.mobile-facets__footer .button--primary');
    applyButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Close the entire drawer
        this.toggleDrawer(false);
      });
    });

    const customCloseButtons = this.querySelectorAll('.mobile-facets__close-custom');
    customCloseButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Close the entire drawer
        this.toggleDrawer(false);
      });
    });
  }

  // Original toggle functionality that works for both desktop and mobile
  toggleDrawer(forceOpen) {
    const isOpening = forceOpen !== undefined ? forceOpen : this.drawer.classList.contains("closed");

    if (isOpening) {
      console.log('opening');
      this.drawer.classList.add("opening");
      this.drawer.classList.remove("closed");
      document.body.classList.add("overflow-hidden-mobile");
      
      // Update all button labels
      this.querySelectorAll(".desktop-facets__open-label").forEach(label => {
        label.textContent = "Close Filters";
      });
    } else {
      console.log('closing');
      this.drawer.classList.add("closing");
      this.drawer.classList.add("closed");
      document.body.classList.remove("overflow-hidden-mobile");
      
      // Reset all mobile submenu states
      const openDetails = this.querySelectorAll('details[open]');
      openDetails.forEach(details => {
        details.classList.remove('menu-opening');
        details.removeAttribute('open');
      });
      
      if (this.mainFacetsContainer) {
        this.mainFacetsContainer.classList.remove('submenu-open');
      }
      
      // Update all button labels
      this.querySelectorAll(".desktop-facets__open-label").forEach(label => {
        label.textContent = "Open Filters";
      });
    }

    // Update accessibility attributes for all buttons
    this.buttons.forEach(button => {
      button.setAttribute("aria-expanded", isOpening);
    });
    
    this.drawer.setAttribute("aria-hidden", !isOpening);

    // Ensure cleanup after animation
    this.drawer.addEventListener("transitionend", () => {
      this.drawer.classList.remove("opening", "closing");

      if (!isOpening) {
        this.drawer.classList.add("closed");
        document.body.classList.remove("overflow-hidden-mobile");
      }
    }, { once: true });
  }
}

customElements.define("filter-drawer", FilterDrawer);


function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu:not(.no-submenu-slidein)');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }
  onBigCloseButtonClick(event) {
    console.log('close button click 3')
    document.querySelector('.header__icon--summary').click();
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
    // trying to fix bug where screen doesn't scroll 
    // because overflow class not being removed
    if (detailsElement.classList.contains('menu-drawer-container')) {
      // console.log('closing drawer')
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    if (detailsElement.id === 'Details-menu-drawer-container') {
      detailsElement.closest('.menu-open').classList.remove('menu-open');
    }
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    
    poster.addEventListener('click', this.loadContent.bind(this));

    /* // Check if mobile and should play inline
    const isMobile = window.innerWidth < 750;
    const shouldPlayInline = poster.hasAttribute('data-inline-video');
    
    if (isMobile && shouldPlayInline) {
      // Play inline on mobile instead of opening modal
      console.log('')
      poster.addEventListener('click', this.loadContent.bind(this, true));
    } else if (isMobile) {
      // Open modal on mobile (default behavior for models, etc.)
      poster.addEventListener('click', this.openModal.bind(this));
    } else {
      // Desktop: always play inline
      poster.addEventListener('click', this.loadContent.bind(this, true));
    } */
  }

  // added
  /* openModal(event) {
    event.preventDefault();
    const mediaId = this.dataset.mediaId;
    const modal = document.querySelector(`#ProductModal-${mediaId}`);
    if (modal) {
      modal.show(event.target);
    }
  } */

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

/* class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
} */
/* 
EDITS for slider dots instead of numbers from:
https://community.shopify.com/c/shopify-design/dawn-theme-change-multi-column-slider-controls-to-dots/m-p/2163391
*/
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    if (!this.slider){
      this.slider = this.querySelector('[class^="Slider-"]');
    }
    // Check if this is a vertical thumbnail slider
    this.isVerticalThumbs = this.classList.contains('thumbnail-slider') && this.closest('.vertical-thumbs');

    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);
    

    // Add orientation change listener
    window.addEventListener('orientationchange', () => {
      // Wait for the browser to finish the orientation change reflow
      setTimeout(() => this.initPages(), 300);
    });

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.sliderControlWrapper = this.querySelector('.slider-buttons');

    if (!this.sliderControlWrapper) return;
    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');


    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
    document.addEventListener('slider_updated', () => {
      // console.log('HEARD slider_updated')
      /* this.resetPages(); */
      this.slider.scrollTo({
        left: 0,
        top: 0
      });
      this.resetPages();
    })

   
    
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;

    /* this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset); */

    if (this.isVerticalThumbs) {
      // For vertical thumbnails, calculate based on height
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
      this.slidesPerPage = Math.floor(this.slider.clientHeight / this.sliderItemOffset);
    } else {
      // Original horizontal logic
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
      
    }

    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    /* this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1; */

    if (this.isVerticalThumbs) {
      // For vertical, calculate based on scrollTop
      this.currentPage = Math.round(this.slider.scrollTop / this.sliderItemOffset) + 1;
    } else {
      // Original horizontal logic
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1]
        }
      }));
    }

    if (this.enableSliderLooping) return;

    // console.log('checking slider thumbs 1', this.isSlideVisible(this.sliderItemsToShow[0]))
    // console.log('checking slider thumbs 2', this.isSlideVisibleVerticalThumbs(this.sliderItemsToShow[0]))
    if (this.isSlideVisible(this.sliderItemsToShow[0])) {
      setTimeout(() => {
        this.prevButton.setAttribute('disabled', 'disabled');
      }, 100)
      
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }


    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1]?.classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1]?.setAttribute('aria-current', true);

  }

  isSlideVisible(element, offset = 0) {
    /* const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft; */
    if (this.isVerticalThumbs) {
      // For vertical thumbnails, check vertical visibility
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return (element.offsetTop + element.clientHeight) <= lastVisibleSlide && 
             element.offsetTop >= this.slider.scrollTop;
    } else {
      // Original horizontal logic
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && 
             element.offsetLeft >= this.slider.scrollLeft;
    }
  }

  /* isSlideVisibleVerticalThumbs(element, offset = 0) {
    console.log('element:', element)
    console.log('offset:', offset)
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    console.log('isSlideVisible values:')
    console.log('lastVisibleSlide:', lastVisibleSlide)
    console.log('element.offsetLeft', element.offsetLeft)
    console.log('element.clientWidth', element.clientWidth)
    console.log('element.clientHeight', element.clientHeight)
    console.log('element.offsetLeft + element.clientWidth:', element.offsetLeft + element.clientWidth)
    console.log('this.slider.scrollLeft:', this.slider.scrollLeft)
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  } */

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;

    /* this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    }); */

    if (this.isVerticalThumbs) {
      // For vertical, scroll top/bottom
      this.slideScrollPosition = event.currentTarget.name === 'next' 
        ? this.slider.scrollTop + (step * this.sliderItemOffset)
        : this.slider.scrollTop - (step * this.sliderItemOffset);
      
      this.slider.scrollTo({
        top: this.slideScrollPosition,
        behavior: 'smooth'
      });
    } else {
      // Original horizontal logic
      this.slideScrollPosition = event.currentTarget.name === 'next' 
        ? this.slider.scrollLeft + (step * this.sliderItemOffset)
        : this.slider.scrollLeft - (step * this.sliderItemOffset);
      
      this.slider.scrollTo({
        left: this.slideScrollPosition,
        behavior: 'smooth'
      });
    }
  }

  linkToSlide(event) {
    event.preventDefault();
    /* const slideScrollPosition = this.slider.scrollLeft + this.slider.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    }); */

    if (this.isVerticalThumbs) {
      const slideScrollPosition = this.slider.scrollTop + 
        this.slider.clientHeight * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
      this.slider.scrollTo({
        top: slideScrollPosition
      });
    } else {
      const slideScrollPosition = this.slider.scrollLeft + 
        this.slider.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
      this.slider.scrollTo({
        left: slideScrollPosition
      });
    }
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) {
        const check_replacement_lenses = document.querySelector('.product-replacement-lenses')
        if (!check_replacement_lenses) selectedSwatchValue.innerHTML = value
      };
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

/**
 * LensColorGuide - Custom Element for handling lens color guide functionality
 * This can be used in both the popup modal and the in-page section
 */
class LensColorGuide extends HTMLElement {
  constructor() {
    super();
    
    // Store references to DOM elements
    this.container = this;
    this.isInitialized = false;
    // this.hasSwiperSlider = false;
    // this.hasSwiperJS = false;
    this.isReplacementLensPDP = false;
  }
  
  connectedCallback() {
    if (this.isInitialized) return;

    /* const check_swiperjs = this.querySelector('.ba-swiper-wrap');
    if (check_swiperjs) this.hasSwiperJS = true; */

    const check_is_replacement_pdp = this.querySelector('.replacement-lens-pdp');
    if (check_is_replacement_pdp) this.isReplacementLensPDP = true;
    
    // Initialize the component
    /* if (this.hasSwiperJS) {
      this.initializeSwiperJS();
    } else {
      this.initializeSlider();
    } */
    this.initializeSwiperSlider();
    
    this.initializeMobileBeforeAfter();
    this.initializeColorSwatches();
    this.checkSelectedTitle();
    
    this.isInitialized = true;
    console.log('mainProduct', mainProduct)
  }
  
  /**
   * Initialize the before-after slider
   */
  /* initializeSlider() {
    const slider = this.querySelector('.ba-range-slider');
    const container = this.querySelector('.ba-container');
    
    if (slider && container) {
      slider.addEventListener('input', (e) => {
        container.style.setProperty('--position', e.target.value + '%');
      });
    }
  } */
  
  initializeSwiperSlider() {
    const slider = this.querySelectorAll('.ba-range-slider');
    const container = this.querySelectorAll('.ba-container');
    
    if (slider.length > 0 && container.length > 0) {
      slider.forEach(el => {
        el.addEventListener('input', (e) => {
          console.log('input')
          el.closest('.ba-container').style.setProperty('--position', e.target.value + '%');
        });
      })
      
    }
  }
  
  /**
   * Check and ensure the correct radio button is selected
   */
  checkSelectedTitle() {
    const selectedValueSpan = this.querySelector('legend.form__label [data-selected-value]');
    if (!selectedValueSpan) return;
    
    const currentTitle = selectedValueSpan.dataset.selectedValue;
    const matchingInput = this.querySelector(`input[type="radio"][value="${currentTitle}"]`);
    
    if (matchingInput) {
      // Reset all inputs
      this.querySelectorAll('input[type="radio"]').forEach(input => {
        input.removeAttribute('checked');
        input.checked = false;
        
        // Remove selected class from labels
        const label = input.nextElementSibling;
        if (label) {
          label.classList.remove('is-selected');
        }
      });
      
      // Set the matching input as checked
      matchingInput.setAttribute('checked', 'checked');
      matchingInput.checked = true;
      
      // Add selected class to label
      const selectedLabel = matchingInput.nextElementSibling;
      if (selectedLabel) {
        selectedLabel.classList.add('is-selected');
      }
      
      // Dispatch change event to update other components
      matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  /**
   * Initialize the color swatch radio buttons
   */
  initializeColorSwatches() {
    const radioButtons = this.querySelectorAll('input[type="radio"]');
    
    radioButtons.forEach(el => {
      el.addEventListener('change', (e) => {
        // Remove selected class from all labels
        this.querySelectorAll('.swatch-input__label').forEach(label => {
          label.classList.remove('is-selected');
        });
        
        // Add selected class to the clicked option's label
        const selectedLabel = e.target.nextElementSibling;
        if (selectedLabel) {
          selectedLabel.classList.add('is-selected');
        }
        
        // Update the lens guide display
        //this.isReplacementLensPDP ? this.updateReplacementLensGuide(e) : this.updateLensGuide(e);
        this.updateReplacementLensGuide(e)
        // this.updateLensGuide(e);
        e.stopImmediatePropagation();
      });
    });
  }
  
  /**
   * Update the lens guide display based on the selected option
   */
  updateLensGuide(e) {
    const title = e.target.value;
    e.preventDefault();
    
    // Find the selected lens variant
    // get polarized option if replacement lens pdp
    let currentLens = mainProduct.variants.filter((el) => el.title.includes(title));
    if (!currentLens.length || !currentLens[0].metafields) return;
    
    let currentLensValues = currentLens[0].metafields;
    console.log('currentLensValues',currentLensValues)
    
    // Update all elements with the new lens data
    const elements = {
      img1: this.querySelector('.before-image'),
      img2: this.querySelector('.after-image'),
      productImage: this.querySelector('.current-lens-image.small-hide img'),
      productImageMobile: this.querySelector('.current-lens-image.medium-up-hide img'),
      shortName: this.querySelector('.after-label'),
      mainTitle: this.querySelector('.lens-color-guide-swatches .form__label'),
      lensDescription: this.querySelector('.lens-color-guide-description'),
      features: this.querySelector('.quick-features'),
      conditionsSvg: this.querySelector('.lens-color-guide-conditions-svg'),
      conditionsText: this.querySelector('.lens-color-guide-conditions-text'),
      vltValue: this.querySelector('.vlt-value'),
      vltInfo: this.querySelector('.vlt-info-link')
    };
    
    if (elements.img1) {
      elements.img1.src = currentLensValues.scene_1_before_image;
      elements.img1.srcset = '';
    }
    
    if (elements.img2) {
      elements.img2.srcset = currentLensValues.scene_1_after_image;
      elements.img2.src = '';
    }
    
    if (elements.productImage) {
      elements.productImage.src = currentLensValues.product_image;
      elements.productImage.srcset = '';
    }
    
    if (elements.productImageMobile) {
      elements.productImageMobile.src = currentLensValues.product_image;
      elements.productImageMobile.srcset = '';
    }
    
    if (elements.shortName) elements.shortName.textContent = currentLensValues.short_name;
    if (elements.mainTitle) elements.mainTitle.textContent = title;
    if (elements.lensDescription) elements.lensDescription.innerHTML = currentLensValues.lens_description_guide;
    if (elements.features) elements.features.innerHTML = currentLensValues.lens_features;
    if (elements.conditionsSvg) elements.conditionsSvg.innerHTML = currentLensValues.conditions_icon_svg;
    if (elements.conditionsText) elements.conditionsText.textContent = currentLensValues.conditions_description;
    if (elements.vltValue) elements.vltValue.textContent = currentLensValues.vlt_value;
    //if (elements.vltInfo) elements.vltInfo.setAttribute('aria-label', currentLensValues.vlt_info_hover_description);
  }

  updateReplacementLensGuide(e) {
    const title = e.target.value;
    e.preventDefault();
    
    // Find the selected lens variant
    // get polarized option if replacement lens pdp
    const isOnPDP = document.body.classList.contains('template--product');
    let currentLensTypeValue;

    /* const currentLensType = document.querySelector('.mega.lens-type input[name="Lens Type-2"]:checked')
    currentLensTypeValue = currentLensType.value */
    if (isOnPDP) {
      const currentLensType = document.querySelector('.mega.lens-type input[name="Lens Type-2"]:checked');
      currentLensTypeValue = currentLensType ? currentLensType.value : null;
      console.log('currentLensType', currentLensType)
      console.log('currentLensTypeValue', currentLensTypeValue)
    }
    let currentLens;
    if (currentLensTypeValue) {
      currentLens = mainProduct.variants.filter(
        (el) => el.title.includes(title) && el.title.includes(`/ ${currentLensTypeValue}`)
      );
    }
    
    // Fallback: match by color title only
    if (!currentLens || !currentLens.length) {
      currentLens = mainProduct.variants.filter((el) => el.title.includes(title));
      if (currentLens.length) {
        currentLensTypeValue = currentLens[0].option2;
      }
    }
    if (!currentLens.length || !currentLens[0].metafields) return;


    /* let currentLens = mainProduct.variants.filter((el) => el.title.includes(title) && el.title.includes(`/ ${currentLensTypeValue}`));
    // fallback if no currentLens, try without the lens type
    if (!currentLens.length > 0) {
      currentLens = mainProduct.variants.filter((el) => el.title.includes(title))
      currentLensTypeValue = currentLens[0].option2;
    };
    console.log('currentLens',currentLens)
    console.log('title',title)

      
    if (!currentLens.length || !currentLens[0].metafields) return; */
    
    let currentLensValues = currentLens[0].metafields;
    
    // Update all elements with the new lens data
    const elements = {
      productImage: this.querySelector('.current-lens-image.small-hide img'),
      productImageMobile: this.querySelector('.current-lens-image.medium-up-hide img'),
      mainTitle: this.querySelector('.lens-color-guide-swatches .form__label .form__label-text'),
      subtitleNonMirrored: this.querySelector('.lens-color-guide-swatches .subtitle-non-mirrored'),
      subtitleMirrored: this.querySelector('.lens-color-guide-swatches .subtitle-mirrored'),
      lensDescription: this.querySelector('.lens-color-guide-description'),
      vltValue: this.querySelector('.vlt-value'),
      vltInfo: this.querySelector('.vlt-info-link'),
      vltBar: this.querySelector('.vlt-bar'),
      vltBarText: this.querySelector('.vlt-bar span.visually-hidden'), //visually-hidden text for screen readers

      activitiesWrap: this.querySelector('.lens-color-guide-summary-lenses'),
      swiperWrap: this.querySelector('.ba-swiper-wrap'),
      main_stage_slider: this.querySelector('.mainStageSwiper .swiper-wrapper'),
      thumbs_slider: this.querySelector('.thumbSwiper .swiper-wrapper')
    };
    
    if (elements.productImage) {
      elements.productImage.src = currentLensValues.product_image;
      elements.productImage.srcset = '';
    }
    if (elements.productImageMobile) {
      elements.productImageMobile.src = currentLensValues.product_image;
      elements.productImageMobile.srcset = '';
    }
    
    if (elements.mainTitle) elements.mainTitle.textContent = `${title} – ${currentLensTypeValue}`;

    if (currentLensValues.mirrored === 'mirrored') {
      if (elements.subtitleMirrored) elements.subtitleMirrored.textContent = title;
      if (elements.subtitleNonMirrored) elements.subtitleNonMirrored.textContent = '';
    } else {
      if (elements.subtitleNonMirrored) elements.subtitleNonMirrored.textContent = title;
      if (elements.subtitleMirrored) elements.subtitleMirrored.textContent = '';
    }
    
    console.log('currentLensValues', currentLensValues)
    if (elements.lensDescription) {
      currentLensValues.lens_description_guide ? elements.lensDescription.innerHTML = currentLensValues.lens_description_guide : elements.lensDescription.innerHTML = '';
    };
    
    if (elements.vltValue) elements.vltValue.textContent = currentLensValues.vlt_value;
    //if (elements.vltInfo) elements.vltInfo.setAttribute('aria-label', currentLensValues.vlt_info_hover_description);
    if (elements.vltBar) {
      /* elements.vltBar.classList.remove('vlt-bar-low');
      elements.vltBar.classList.remove('vlt-bar-medium');
      elements.vltBar.classList.remove('vlt-bar-bright');
      elements.vltBar.classList.add(`vlt-bar-${currentLensValues.brightness_rating?.toLowerCase()}`); */
      elements.vltBar.style.setProperty('--vlt-bar-start', currentLensValues.brightness_bar_start)
      elements.vltBar.style.setProperty('--vlt-bar-end', currentLensValues.brightness_bar_end)
      elements.vltBarText.textContent = currentLensValues.lens_conditions_recommendation;
    }

    if (elements.activitiesWrap) {
      const previous_summaries = elements.activitiesWrap.querySelectorAll('.summary-activity')
      if (previous_summaries.length > 0) previous_summaries.forEach(el => el.remove());

      
      const fragment = document.createDocumentFragment();

      if (currentLensValues.recommended_activities?.length > 0) {
        currentLensValues.recommended_activities.forEach(item => {
          const summary_template = this.querySelector('template#summary-activity');
          const summary_template_clone = summary_template.content.cloneNode(true);

          const activity_span = summary_template_clone.querySelector('.summary-activity-icon')
          const activity_icon = summary_template_clone.querySelector('.summary-activity-icon img')
          const activity_text = summary_template_clone.querySelector('.summary-activity-text')
          item.ba_activity_icon ? activity_span.removeAttribute('hidden') : activity_span.setAttribute('hidden', true)
          activity_icon.src = item.ba_activity_icon
          activity_text.textContent = item.ba_activity_text

          fragment.appendChild(summary_template_clone);
        })
      }
      elements.activitiesWrap.appendChild(fragment);
    }


    if (elements.swiperWrap) {
      const previous_slides = elements.swiperWrap.querySelectorAll('.swiper-slide')
      if (previous_slides.length > 0) previous_slides.forEach(el => el.remove());

      const fragment = document.createDocumentFragment();
      const fragment2 = document.createDocumentFragment();

      if (currentLensValues.ba_pairs?.length > 0) {
        currentLensValues.ba_pairs.forEach(item => {
          const main_stage_template = this.querySelector('template#ba-main');
          const main_stage_template_clone = main_stage_template.content.cloneNode(true);

          const before_image = main_stage_template_clone.querySelector('.before-image')
          const before_text = main_stage_template_clone.querySelector('.before-label')
          const after_image = main_stage_template_clone.querySelector('.after-image')
          const after_text = main_stage_template_clone.querySelector('.after-label')
          before_image.src = item.ba_pair_before_image
          before_image.srcset = ''
          before_text.textContent = item.ba_pair_before_text
          after_image.src = item.ba_pair_after_image
          after_image.srcset = ''
          after_text.textContent = item.ba_pair_after_text

          fragment.appendChild(main_stage_template_clone);


          const thumbs_template = this.querySelector('template#ba-thumb');
          const thumbs_template_clone = thumbs_template.content.cloneNode(true);
          const thumb_image = thumbs_template_clone.querySelector('.ba-thumb')
          // console.log('item::::', item.src)
          thumb_image.src = item.ba_pair_before_image
          thumb_image.srcset = ''


          fragment2.appendChild(thumbs_template_clone);
        })
      }
      elements.main_stage_slider.appendChild(fragment);
      elements.thumbs_slider.appendChild(fragment2);
    }
    this.initializeSwiperSlider();
    this.initializeMobileBeforeAfter();
    // console.log('dispatching update_swipers')
    document.body.dispatchEvent(new CustomEvent('update_swipers'));
  }
  
  /**
   * Show the "before" image in the comparison view
   */
  showBefore(e) {
    const button = e.target;
    const button_parent = button.closest('.ba-container');
    /* console.log('showBefore e:', e)
    console.log('showBefore this:', this) */
    const elements = {
      /* beforeImage: this.querySelector('.before-image'),
      beforeImageLabel: this.querySelector('.before-label'),
      afterImageLabel: this.querySelector('.after-label'),
      buttonContent: this.querySelector('.mobile-before-after-button-content') */
      beforeImage: button_parent.querySelector('.before-image'),
      beforeImageLabel: button_parent.querySelector('.before-label'),
      afterImageLabel: button_parent.querySelector('.after-label'),
      buttonContent: button_parent.querySelector('.mobile-before-after-button-content')
    };
    
    if (elements.beforeImage) elements.beforeImage.style.opacity = 1;
    if (elements.beforeImageLabel) elements.beforeImageLabel.style.opacity = 1;
    if (elements.afterImageLabel) elements.afterImageLabel.style.opacity = 0;
    if (elements.buttonContent) elements.buttonContent.style.opacity = 0;
  }
  
  /**
   * Hide the "before" image in the comparison view
   */
  hideBefore(e) {
    const button = e.target;
    const button_parent = button.closest('.ba-container');
    const elements = {
      /* beforeImage: this.querySelector('.before-image'),
      beforeImageLabel: this.querySelector('.before-label'),
      afterImageLabel: this.querySelector('.after-label') */
      beforeImage: button_parent.querySelector('.before-image'),
      beforeImageLabel: button_parent.querySelector('.before-label'),
      afterImageLabel: button_parent.querySelector('.after-label')
    };
    
    if (elements.beforeImage) elements.beforeImage.style.opacity = 0;
    if (elements.beforeImageLabel) elements.beforeImageLabel.style.opacity = 0;
    if (elements.afterImageLabel) elements.afterImageLabel.style.opacity = 1;
  }
  
  /**
   * Initialize the mobile before-after functionality
   */
  initializeMobileBeforeAfter() {
    // const mobileButton = this.querySelector('.mobile-before-after-button');
    /* mobileButton.addEventListener('touchstart', this.showBefore.bind(this));
    mobileButton.addEventListener('touchend', this.hideBefore.bind(this));
    mobileButton.addEventListener('mousedown', this.showBefore.bind(this));
    mobileButton.addEventListener('mouseup', this.hideBefore.bind(this)); */
    // if (this.isReplacementLensPDP) return;
    const mobileButton = this.querySelectorAll('.mobile-before-after-button');
    // console.log('mobileButton', mobileButton)
    if (mobileButton.length > 0) {
      mobileButton.forEach(el => {
        el.addEventListener('touchstart', this.showBefore.bind(this));
        el.addEventListener('touchend', this.hideBefore.bind(this));
        el.addEventListener('mousedown', this.showBefore.bind(this));
        el.addEventListener('mouseup', this.hideBefore.bind(this));
      })
    }
    
  }
  
  /**
   * Refresh the component (for product:info:refresh events)
   */
  refresh() {
    // this.initializeSlider();
    this.initializeSwiperSlider();
    this.initializeMobileBeforeAfter();
  }
}

// Register the custom element
customElements.define('lens-color-guide', LensColorGuide);

// Add global event listener for product:info:refresh
document.addEventListener('product:info:refresh', () => {
  // Wait for the content to be updated before reinitializing
  setTimeout(() => {
    document.querySelectorAll('lens-color-guide').forEach(guide => {
      guide.refresh();
    });
  }, 3000);
});

// Initialize any existing components when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Automatically initialize any existing lens color guides
  document.querySelectorAll('lens-color-guide').forEach(guide => {
    // The connectedCallback will handle initialization
  });
});

// Run initialization immediately if DOM is already loaded
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  document.querySelectorAll('lens-color-guide').forEach(guide => {
    guide.connectedCallback();
  });
}

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
class ReadMore extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    let _self = this;

    const readMoreButton = _self.querySelector('.read-more-button');
    const description = _self.querySelector('.expanded-description');
    readMoreButton.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('read more click');
      if (description.classList.contains('expanded')) {
        description.classList.remove('expanded');
        readMoreButton.textContent = 'Read more';
        // var anchor = _self.querySelector('#description--{{ section.id }}');
        var offset = 0; // sticky nav height
        window.scroll({ top: (_self.offsetTop - offset), left: 0, behavior: 'smooth' });
      } else {
        description.classList.add('expanded');
        readMoreButton.textContent = 'Read less';
      }
    });
  }
}

customElements.define('read-more', ReadMore);


// for ADA - improved version
function initYotpoADA() {
  const targetNode = document.querySelector('.yotpo-widget-instance');
  
  if (!targetNode) {
    console.warn('Yotpo widget instance not found');
    return;
  }

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        console.log('Yotpo widgets loaded!');
        
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          const yotpoNav = targetNode.querySelector('nav');
          if (yotpoNav) {
            yotpoNav.setAttribute('aria-label', 'Product reviews pagination');
            console.log('Yotpo navigation aria-label set');
          }
        });
        
        // Disconnect after first successful update to prevent repeated checks
        observer.disconnect();
      }
    });
  });

  observer.observe(targetNode, { 
    childList: true, 
    subtree: true // Also observe descendant elements
  });
  
  // Failsafe: disconnect observer after 10 seconds to prevent memory leaks
  setTimeout(() => {
    observer.disconnect();
  }, 10000);
}

// Run when DOM is ready, but don't block
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initYotpoADA);
} else {
  // DOM already loaded, run with a small delay to ensure Yotpo has initialized
  setTimeout(initYotpoADA, 100);
}

/**
 * Initialize scroll progress indicators for lens guide swatch lists
 */
function initializeScrollProgress() {
  const swatchWrappers = document.querySelectorAll('.lens-swatches-with-progress-bar, .lens-features-wrap');
  console.log('swatchWrappers', swatchWrappers)

  if (!swatchWrappers.length > 0) return;
  
  swatchWrappers.forEach(wrapper => {
    // Find the swatch list inside the wrapper
    const swatchList = wrapper.querySelector('.content-has-progress-bar');
    
    if (!swatchList) return;
    
    // Find the progress container inside the wrapper
    const progressContainer = wrapper.querySelector('.slider-progress-container');
    
    if (!progressContainer) return;
    
    const progressIndicator = progressContainer.querySelector('.slider-progress-indicator');
    
    if (!progressIndicator) return;
    
    let first = true;
    let initialWidth;
    // Function to update progress based on scroll position
    const updateProgress = () => {
      const scrollLeft = swatchList.scrollLeft;
      const scrollWidth = swatchList.scrollWidth;
      const clientWidth = swatchList.clientWidth;
      
      // Calculate the maximum scrollable distance
      const maxScroll = scrollWidth - clientWidth;
      
      // Add or remove has-overflow class on the wrapper
      if (maxScroll <= 0) {
        wrapper.classList.remove('has-overflow');
        progressIndicator.style.width = '100%';
        progressContainer.setAttribute('aria-valuenow', '100');
        return;
      } else {
        wrapper.classList.add('has-overflow');
      }
      
      // Calculate the percentage of content visible
      const visiblePercentage = (clientWidth / scrollWidth) * 100;
      
      // Calculate scroll progress (0 to 1)
      const scrollProgress = scrollLeft / maxScroll;
      
      // Calculate final width: visible percentage + (remaining percentage * scroll progress)
      const remainingPercentage = 100 - visiblePercentage;
      const finalWidth = visiblePercentage + (remainingPercentage * scrollProgress);
      const finalWidthRounded = Math.round(finalWidth)
      
      progressIndicator.style.width = `${finalWidth}%`;
      progressContainer.setAttribute('aria-valuenow',finalWidthRounded);


      if (wrapper.classList.contains('lens-features-wrap')) {
        const features_table = wrapper.querySelector('table')
        if (first) {
          initialWidth = finalWidthRounded;
        }
        first = false;

        if (initialWidth === finalWidthRounded) {
          features_table.classList.remove('scrolling-shadow-right')
        } else {
          features_table.classList.add('scrolling-shadow-right')
        }
      }
    };
    
    // Update on scroll
    swatchList.addEventListener('scroll', updateProgress);
    
    // Update on resize (in case container size changes)
    const resizeObserver = new ResizeObserver(updateProgress);
    resizeObserver.observe(swatchList);
    
    // Initial update
    updateProgress();
  });
}
// Run when DOM is ready, but don't block
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeScrollProgress);
} else {
  initializeScrollProgress();
}
document.body.addEventListener('variant_updated', () => {
  setTimeout(initializeScrollProgress, 100);
})
document.body.addEventListener('shopify:section:load', () => {
  setTimeout(initializeScrollProgress, 100);
})

function fixWrappingTitle() {
  const product_title = document.querySelectorAll('.template--product .product__title h1')
  if (product_title.length > 0) {
    product_title.forEach(title => {
      title.textContent = title.textContent.replace('Revant Optics', 'Revant\xA0Optics')
    });
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fixWrappingTitle);
} else {
  fixWrappingTitle();
}


function syncVerticalThumbsHeight() {
  console.log('running syncVerticalThumbsHeight')
  const verticalThumbsContainers = document.querySelectorAll('.vertical-thumbs media-gallery');

  if ( !verticalThumbsContainers.length > 0 ) return;
  
  if (window.innerWidth < 990) {
    // Clear any heights set during landscape and bail
    verticalThumbsContainers.forEach(container => {
      container.querySelector('.desktop-gallery-slider')?.style.removeProperty('height');
      container.querySelector('.thumbnail-slider')?.style.removeProperty('height');
      container.querySelector('.thumbnail-slider .thumbnail-list')?.style.removeProperty('height');
    });
    return;
  }
  // console.log('running syncVerticalThumbsHeight')
  
  verticalThumbsContainers.forEach(container => {
    const desktopGalleryWrap = container.querySelector('.desktop-gallery-slider');
    const desktopGallery = container.querySelector('.desktop-gallery-slider .product__media-list');
    const thumbnailSliderWrap = container.querySelector('.thumbnail-slider');
    const thumbnailSlider = container.querySelector('.thumbnail-slider .thumbnail-list');
    
    if (!desktopGallery || !thumbnailSlider) return;
    
    const updateHeight = () => {
      if (window.innerWidth < 990) {
        desktopGalleryWrap.style.removeProperty('height');
        thumbnailSliderWrap.style.removeProperty('height');
        thumbnailSlider.style.removeProperty('height');
        return;
      }
      // Get the width of the desktop gallery (which will become the height when rotated)
      const galleryHeight = desktopGallery.offsetHeight;
      
      // Set the thumbnail slider height to match the gallery width
      desktopGalleryWrap.style.height = `${galleryHeight}px`;
      thumbnailSliderWrap.style.height = `${galleryHeight}px`;
      thumbnailSlider.style.height = `${galleryHeight}px`;
      // console.log('galleryHeight', galleryHeight)
    };
    
    // Update on load
    updateHeight();
    
    // Update on resize
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(desktopGallery);
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncVerticalThumbsHeight);
} else {
  syncVerticalThumbsHeight();
}

// Also run on variant changes or section loads
document.body.addEventListener('variant_updated', () => {
  setTimeout(syncVerticalThumbsHeight, 100);
});

document.body.addEventListener('shopify:section:load', () => {
  setTimeout(syncVerticalThumbsHeight, 100);
});

window.addEventListener('orientationchange', () => {
  setTimeout(syncVerticalThumbsHeight, 300);
});
